<template>
    <VJoyCollapse>
        <VJoyCollapseItem header="General" open>
            <div slot="collapse-detail">
                <div class="joy-mb-4">
                    <ul v-if="profileAdmin">
                        <li>
                            Account Creation date:
                            <strong>{{ formatDate(profileAdmin.accountCreationDate) }}</strong>
                        </li>
                        <li>
                            Profile creation date:
                            <strong>{{ formatDate(profileAdmin.profileCreationDate) }}</strong>
                        </li>
                        <li>
                            Email:
                            <strong>{{ profileAdmin.email }}</strong>
                        </li>
                        <li>
                            Phone :
                            <strong>{{ profileAdmin.phoneNumber || 'empty' }}</strong>
                        </li>
                        <li>
                            Last modification:
                            <strong>Since {{ since(profileAdmin.lastModification) }}</strong>
                        </li>
                        <li>
                            Last connexion:
                            <strong>Since {{ since(profileAdmin.lastLogin) }}</strong>
                        </li>
                        <li>
                            Locale:
                            <strong>{{ profileAdmin.locale }}</strong>
                        </li>
                        <li>
                            Default Host:
                            <strong>{{ profileAdmin.defaultHost }}</strong>
                        </li>
                    </ul>

                    Links :
                    <ul>
                        <li>
                            <VJoyLink :href="complianceLink">Legal docs</VJoyLink>
                        </li>
                        <li>
                            <VJoyLink :href="statsLink">Stats</VJoyLink>
                        </li>
                        <li>
                            <VJoyLink :href="dashboardLink">Dashboard</VJoyLink>
                        </li>
                        <li>
                            <VJoyLink :href="shakeLink">Shake Page</VJoyLink>
                        </li>
                    </ul>
                </div>
            </div>
        </VJoyCollapseItem>
        <VJoyCollapseItem header="Admin Notes" open>
            <div slot="collapse-detail">
                <div class="joy-mb-4">
                    <ul>
                        <li v-for="event in adminEvents?.events" :key="event.id">
                            <VJoyWrapper justify="space-between">
                                <div>
                                    <h4>{{ event.date }} - {{ event.authorName }}: {{ event.type }}</h4>
                                    <!-- eslint-disable-next-line vue/no-v-html -->
                                    <span v-html="event.comment"></span>
                                </div>
                                <VJoyDialogTrigger v-if="event.type === 'SIMPLE_COMMENT'" :dialog="eventDeleteDialog" @click="SelectedNote = event">
                                    <VJoyIcon name="trash" color="error" role="button" />
                                </VJoyDialogTrigger>
                            </VJoyWrapper>
                        </li>
                    </ul>
                    <form v-if="isLegalTeam" @submit.prevent="addNote">
                        <VJoyTextarea v-model="note" :minlength="10" :maxlength="300" placeholder="Add a note" />
                        <VJoyButton variant="primary" type="submit">Add a note</VJoyButton>
                    </form>
                </div>
            </div>
        </VJoyCollapseItem>
        <VJoyCollapseItem v-if="devDetails" header="Dev" open>
            <div slot="collapse-detail">
                <div class="joy-mb-4">
                    <ul>
                        <li>accountId: {{ devDetails.accountId }}</li>
                        <li>companyId:{{ devDetails.companyId }}</li>
                        <li>identityId:{{ devDetails.identityId }}</li>
                        <li>profileId:{{ devDetails.profileId }}</li>
                        <li>
                            <VJoyLink href="#" @click.prevent="reindex">Reindex</VJoyLink>
                        </li>
                        <li v-if="isViewerMemberOfTechnicalSupport">
                            <VJoyLink :href="fixTurnoverLink">Fix turnover (investigate first !!!!)</VJoyLink>
                        </li>
                    </ul>
                </div>
            </div>
        </VJoyCollapseItem>
    </VJoyCollapse>
    <VJoyDialog ref="eventDeleteDialog" confirm-text="confirm" cancel-text="cancel" @dialog:confirm="removeSelectedNote">
        <template #dialog-header>Are you sure you want to delete this event?</template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {formatDistance, formatISO, parseISO} from 'date-fns';
    import {storeToRefs} from 'pinia';
    import {useGuestHost, useHasEnoughPriviledge, useUniversalDelete, useUniversalPost} from '#imports';
    import {
        VJoyButton,
        VJoyCollapse,
        VJoyCollapseItem,
        VJoyDialog,
        VJoyDialogTrigger,
        VJoyIcon,
        VJoyLink,
        VJoyTextarea,
        VJoyWrapper,
    } from '@maltjoy/core-vue';
    import {extractLink} from '~/helpers/api/ExtractLinkHelper';
    import {useProfileAdminStore} from '~/store/profileAdmin.store';
    import {useProfileStore} from '~/store/profileStore';
    import type {AdminEventResource} from '~/types/api-raw-types';

    const isViewerMemberOfTechnicalSupport = await useHasEnoughPriviledge(['ROLE_TECHNICAL_SUPPORT']);
    const isAdmin = await useHasEnoughPriviledge(['ROLE_ADMIN']);
    const isLegalTeam = await useHasEnoughPriviledge(['ROLE_COMPLIANCE_DOCS']);

    // Stores
    const profileStore = useProfileStore();
    const {getDevDetails} = profileStore;
    const {devDetails, profile} = storeToRefs(profileStore);

    const adminEventsStore = useProfileAdminStore();
    const {addAdminEvent, getAdminEvents, getProfileAdmin} = adminEventsStore;
    const {adminEvents, profileAdmin} = storeToRefs(adminEventsStore);

    if (isAdmin) {
        await getDevDetails();
        await getProfileAdmin();
        await getAdminEvents();
    }

    const note = ref('');
    const SelectedNote = ref<AdminEventResource>();
    const eventDeleteDialog = ref<InstanceType<typeof VJoyDialog>>();
    const complianceLink = computed(() => `${extractLink(profileAdmin.value!.links, 'compliance')}?immersion=${profileAdmin.value!.identityId}`);
    const statsLink = computed(() => extractLink(profileAdmin.value!.links, 'stats'));
    const dashboardLink = computed(() => extractLink(profileAdmin.value!.links, 'dashboard'));
    const shakeLink = computed(() => extractLink(profileAdmin.value!.links, 'shake'));
    const fixTurnoverLink = computed(() => extractLink(devDetails.value!.links, 'fixTurnover'));

    function addNote() {
        addAdminEvent({type: 'SIMPLE_COMMENT', comment: note.value}, profile.value!).then(async () => {
            await getAdminEvents();
            note.value = '';
        });
    }

    function reindex() {
        const reindexUrl = extractLink(devDetails.value!.links, 'reindex');
        useUniversalPost(`${useGuestHost()}${reindexUrl}`);
    }
    function formatDate(date: string) {
        return formatISO(parseISO(date), {representation: 'date'});
    }
    function since(date: string) {
        return formatDistance(parseISO(date), new Date());
    }
    function removeSelectedNote() {
        if (adminEvents.value?.events) {
            const removeNoteUrl = extractLink(SelectedNote.value!._links, 'delete');
            useUniversalDelete(removeNoteUrl);
            eventDeleteDialog.value!.hide();
            adminEvents.value.events = adminEvents.value.events.filter((item) => item.id !== SelectedNote.value?.id);
        }
    }
</script>

<style lang="scss" scoped>
    h4 {
        margin-bottom: 6px;
    }

    span {
        white-space: pre-wrap;
    }
</style>
